import { shovelAiApi } from '@api/shovelAiApi';
import { TextareaInput, TextInput } from '@components/common/inputs';
import { usePreviewAccess } from '@hooks/usePreviewAccess';
import { RootState } from '@state/types';
import { AI_SULLABUS_PARSING_DEVELOPER_MESSAGE, AI_SULLABUS_PARSING_MODEL } from '@utils/syllabusImport';
import { FC, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Loader from 'react-loader-spinner';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import styled from 'styled-components';
import { ExportCourseResponse } from '../../api/lambdaApi';
import t from '../../i18n/t';
import { getImportCoursePreview } from '../../state/calendar/selectors';
import * as courseActions from '../../state/course/actions';
import { setLastSyllabusImportAt } from '../../state/course/actions';
import colors from '../../utils/colors';
import { LargeText, MediumText, RegularText } from '../../utils/typography';
import CheckBox from '../common/CheckBox';
import { Column, Row } from '../common/layoutUtils';
import ModalDialog from '../common/ModalDialog';
import ImportCourseSyllabusPreview from './ImportCourseSyllabusPreview';
import { alert, NotificationMode } from '../common/snackbarNotifications/Notifications';
import { getTimeZone } from '@state/semester/selectors';

type PropsFromState = {
  opened: boolean;
  courseId?: number;
  courseCorrelationId?: string;
  courseName?: string;
  courseColorHex?: string;
  data?: ExportCourseResponse;
};

type PropsFromDispatch = {
  importCourse: typeof courseActions.importCourse.request;
  closeImportCourseDialog: typeof courseActions.closeImportCourseDialog;
};

type Props = PropsFromState & PropsFromDispatch;

const MAX_PDF_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_PDF_SIZE_MB * 1024 * 1024;

const ImportCourseSyllabusFilePickerDialog: FC<Props> = props => {
  const [data, setData] = useState<ExportCourseResponse | null>(null);

  const onClose = useCallback(() => {
    setData(null);
    props.closeImportCourseDialog();
  }, []);

  const { opened, courseName, courseId, courseColorHex } = props;

  return (
    <ModalDialog
      open={opened}
      onClose={() => onClose()}
      size={'lg'}
      titleComponent={
        <FilePickerDialogTitle>
          {t.IMPORT_SYLLABUS} to <span style={{ color: courseColorHex }}>{courseName}</span>
        </FilePickerDialogTitle>
      }
      contentStyle={{ paddingLeft: '100px', paddingRight: '100px' }}
      fullWidth
    >
      {!!data ? (
        <ImportCourseSyllabusPreview data={data} onClose={onClose} />
      ) : (
        <SyllabusFilePicker onFileParsed={setData} courseId={courseId} closeDialog={onClose} />
      )}
    </ModalDialog>
  );
};

function SyllabusFilePicker({
  onFileParsed,
  courseId,
  closeDialog
}: {
  onFileParsed: (course: ExportCourseResponse) => void;
  courseId?: number;
  closeDialog?: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [parsingInProgress, setParsingInProgress] = useState(false);

  const dispatch = useDispatch();

  const timezone = useSelector(getTimeZone);

  const hasPreviewAccess = usePreviewAccess();

  const [syllabusParsingInstructions, setSyllabusParsingInstructions] = useState<{
    extractText: boolean;
    developerMessage: string;
    model: string;
  }>({
    extractText: false,
    developerMessage: AI_SULLABUS_PARSING_DEVELOPER_MESSAGE,
    model: AI_SULLABUS_PARSING_MODEL
  });

  const handleShovelFile = (file: File) => {
    setError(null);
    const fr = new FileReader();
    fr.readAsText(file);
    fr.onload = () => {
      try {
        const course = JSON.parse(String(fr.result));
        onFileParsed(course);
      } catch {
        setError('Invalid .shovel file (expected JSON export).');
      }
    };
    fr.onerror = () => setError(fr.error?.message || 'Failed to read .shovel file.');
  };

  const handleSyllabusFile = async (file: File) => {
    setError(null);

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`PDF is too large. Maximum allowed size is ${MAX_PDF_SIZE_MB} MB.`);
      return;
    }

    setParsingInProgress(true);

    try {
      const getUploadUrlRes = await shovelAiApi.getSyllabusFileUploadUrl(file.name, file.type);

      if (!getUploadUrlRes.ok || !getUploadUrlRes.data) {
        setError('Failed to parse file.');
        setParsingInProgress(false);
        return;
      }

      const { uploadUrl, fileName } = getUploadUrlRes.data;

      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      });

      if (!uploadRes.ok) {
        setError('Failed to upload file.');
        setParsingInProgress(false);
        return;
      }

      const parseRes = await shovelAiApi.parseSyllabus(
        hasPreviewAccess
          ? {
              fileName,
              courseId,
              timezone,
              extractText: syllabusParsingInstructions.extractText,
              developerMessage: syllabusParsingInstructions.developerMessage,
              model: syllabusParsingInstructions.model
            }
          : { fileName, courseId, timezone }
      );

      if (!parseRes.ok) {
        setError('Failed to parse file.');
        setParsingInProgress(false);
        return;
      }

      dispatch(setLastSyllabusImportAt(new Date()));

      setParsingInProgress(false);

      alert('Processing syllabus file. This can take up to 5 minutes', NotificationMode.INFO);
      dispatch(courseActions.closeCourseDetailsDialog());

      closeDialog();
    } catch (e) {
      setError(e.message || 'Failed to parse file.');
      setParsingInProgress(false);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const isShovelFile = file.name.toLowerCase().endsWith('.shovel');

      if (isShovelFile) {
        handleShovelFile(file);
      } else {
        handleSyllabusFile(file);
      }
    },
    [handleShovelFile, handleSyllabusFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: () => {
      alert('Unsupported file type. Convert your file to PDF before uploading.');
    },
    accept: {
      // PDF (incl. Google Docs exported as PDF, web pages saved as PDF)
      'application/pdf': ['.pdf'],

      // // Word
      // 'application/msword': ['.doc'],
      // 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],

      // // PowerPoint
      // 'application/vnd.ms-powerpoint': ['.ppt'],
      // 'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],

      // // Excel (incl. Google Sheets exported as Excel)
      // 'application/vnd.ms-excel': ['.xls'],
      // 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],

      // // CSV (incl. Google Sheets exported as CSV)
      // 'text/csv': ['.csv'],

      // // Text files
      // 'text/plain': ['.txt'],

      // // RTF
      // 'application/rtf': ['.rtf'],
      // 'text/rtf': ['.rtf'],

      // // Apple Pages
      // 'application/vnd.apple.pages': ['.pages'],
      // 'application/octet-stream': ['.pages'], // fallback (some browsers)
      // 'application/zip': ['.pages'],

      // Shovel syllabi (.shovel – assuming JSON-based)
      'application/json': ['.shovel']

      // // Images (for “clear images”)
      // 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic']
    },
    multiple: false,
    maxSize: MAX_FILE_SIZE_BYTES
    // maxSize: mode === 'pdf' ? MAX_FILE_SIZE_BYTES : undefined,
  });

  return (
    <Column>
      <RegularText>Upload your syllabus PDF file.</RegularText>
      {parsingInProgress ? (
        <LoaderWrapper>
          <Row style={{ alignItems: 'center' }}>
            <Loader type="Oval" color={colors.primaryPurple} height={14} width={14} style={{ display: 'flex' }} />
            <MediumText style={{ marginLeft: 6 }}>Uploading syllabus file...</MediumText>
          </Row>
        </LoaderWrapper>
      ) : (
        <DropArea {...getRootProps()} $active={isDragActive}>
          <input {...getInputProps()} />
          <RegularText style={{ fontWeight: 'bold' }}>
            {isDragActive ? 'Drop it here...' : 'Click or drag your file here'}
          </RegularText>
        </DropArea>
      )}
      <Column style={{ alignItems: 'center', marginTop: 10 }}>
        {error && <MediumText color={colors.negative}>{error}</MediumText>}
      </Column>
      {hasPreviewAccess && (
        <div>
          <RegularText strong>Syllabus Parsing Instructions</RegularText>
          <div style={{ marginBottom: 10, marginTop: 10 }}>
            <CheckBox
              checked={syllabusParsingInstructions.extractText}
              onChange={() => setSyllabusParsingInstructions(prev => ({ ...prev, extractText: !prev.extractText }))}
              label={'Extract text manually'}
            />
          </div>
          <TextInput
            label="Model"
            value={syllabusParsingInstructions.model}
            onChange={e => setSyllabusParsingInstructions(prev => ({ ...prev, model: e.target.value }))}
          />
          <TextareaInput
            label="Developer Message"
            value={syllabusParsingInstructions.developerMessage}
            onChange={e => setSyllabusParsingInstructions(prev => ({ ...prev, developerMessage: e.target.value }))}
            minRows={20}
            maxRows={20}
          />
        </div>
      )}
    </Column>
  );
}

const mapStateToProps = (state: RootState) => getImportCoursePreview(state);

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      importCourse: courseActions.importCourse.request,
      closeImportCourseDialog: courseActions.closeImportCourseDialog
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ImportCourseSyllabusFilePickerDialog);

const FilePickerDialogTitle = styled(LargeText)`
  display: block;
  text-align: center;
  margin: 30px 0 8px;
`;

const DropArea = styled.div<{ $active: boolean }>`
  margin-top: 20px;
  border: 2px dashed ${({ theme, $active }) => ($active ? theme.primary : theme.borderColor)};
  background: ${props => (props.$active ? props.theme.backgroundDark : 'transparent')};
  border-radius: 16px;
  padding: 32px 24px;
  transition: all 120ms ease-in-out;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
`;

const LoaderWrapper = styled.div`
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
